import { toast } from 'react-toastify';
import UserDetail from './UserDetails';
import UserRole from './UserRoles';
import UserPermission from './UserPermissions';
import type { PermissionGroup, UserBranches, UserDetails, UserRoles } from './models/UserInterfaces';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';


const UserData = () => {
    let navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("Details");
    const initUserDetails: UserDetails = {
        id: 0,
        firstName: "",
        lastName: "",
        companyId: -1,
        departmentId: -1,
        extension: "",
        mobile: "",
        email: "",
        emailSignatureId: -1,
        userName: "",
        password: "",
        confirmPassword: "",
        driverSystem: false,
        skipTruckToolCheck: false,
        statusId: 1
    };
    const [userDetails, setUserDetails] = useState<UserDetails>(initUserDetails);
    const [userRoles, setUserRoles] = useState<UserRoles[]>([]);
    const [userBranches, setUserBranches] = useState<UserBranches[]>([]);
    const [userPermissions, setUserPermissions] = useState<PermissionGroup[]>([]);
               
    useEffect(() => {
        window.scrollTo(0, 0);
        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("CreateEditUsers");
        if (hasPermission) {
            getUserDetails();
        } else {
            navigate("/accessdenied");
        }
    }

    const getUserDetails = async () => {
        //get user details
        if (Number(id) > 0) {
            const apiUrl = "GetUserDetails?userId=" + Number(id);
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                let userDet = result.data.userDetails;
                userDet.password = "";  //used when adding new user
                userDet.confirmPassword = "";

                setUserDetails(userDet);
                setUserRoles(result.data.userRoles);
                setUserBranches(result.data.userBranches);
                setUserPermissions(result.data.userPermissions);
                setLoading(false);
            };
        } else {
            const apiUrl = "GetPermissions";
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                setUserPermissions(result.data.permissionGroups);
                setLoading(false);
            };
        } 
    }

    const tabSelected = (updatedTab: string) => {
        setTab(updatedTab);
    }

    const tabChanged = (e: any) => {
        tabSelected(e.target.value);
    }

    const noChange = (_e: any) => {
        //no changes as user doesn't actually click on the radio button
    }

    const updateDetailsText = (fieldName: string, fieldValue: string) => {
        setUserDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateDetailsNumber = (fieldName: string, fieldValue: number) => {
        setUserDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const addRole = (newRole: UserRoles) => {
        setUserRoles([...userRoles, newRole]); 
    }

    const removeRole = (id: number) => {
        setUserRoles((prevItems) => prevItems.filter((item) => item.id !== id));
    }

    const addBranch = (newBranch: UserBranches) => {
        setUserBranches([...userBranches, newBranch]);
    }

    const removeBranch = (id: number) => {
        setUserBranches((prevItems) => prevItems.filter((item) => item.id !== id));
    }

    const updateDriverSystem = (driverSystem: boolean, skipTruckToolCheck: boolean) => {
        setUserDetails(prevState => ({ ...prevState, driverSystem: driverSystem, skipTruckToolCheck: skipTruckToolCheck }));
    }

    const updatePermissions = (groupId: number, id: number, selected: boolean) => {
        setUserPermissions(prevState =>
            prevState.map(grp => {
                if (grp.groupId === groupId) {
                    // Found the correct category, now update the sublist
                    return {
                        ...grp,
                        permissions: grp.permissions.map(subItem =>
                            subItem.id === id
                                ? { ...subItem, selected: selected }
                                : subItem
                        )
                    };
                }
                return grp; // Return permission group unchanged
            })
        );
    }

    const validateAll = () => {
        let formIsValid = true;

        if (!userDetails.firstName || !userDetails.lastName || userDetails.departmentId < 1 || !userDetails.email || !userDetails.userName || userDetails.statusId < 0) {
            toast.error("User Details must be completed!");
            formIsValid = false;
        }
        if (Number(id) == 0) {
            //check password - if existing user, separate form to change password
            if (!userDetails.password || !userDetails.confirmPassword) {
                toast.error("User Details - Password and Confirm Password are required");
                formIsValid = false;
            }
            if (userDetails.password && userDetails.confirmPassword) {
                //check password and confirm password match
                if (userDetails.password != userDetails.confirmPassword) {
                    toast.error("User Details - Password and Confirm Password must match");
                    formIsValid = false;
                }
            }
        }

        if (userRoles.length < 1) {
            toast.error("Roles/Branches - At least one role is required");
            formIsValid = false;
        }

        if (userBranches.length < 1) {
            toast.error("Roles/Branches - At least one branch is required");
            formIsValid = false;
        }
        return formIsValid;
    }

    const save = () => {
        //validate all data before saving
        if (validateAll()) {
            saveUser();
        }
    }

    const saveUser = async () => {
        var regUser = {
            userDetails: userDetails,
            userRoles: userRoles,
            userBranches: userBranches,
            userPermissions: userPermissions
        };
        
        if (Number(id) == 0) {
            var jsonItem = JSON.stringify(regUser);

            const apiUrl = "RegisterUser";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    toast.success("User Saved");
                    //then need to go to job search?
                    navigate("/usersearch");
                }

            };
        } else {
            var jsonItem = JSON.stringify(regUser);

            const apiUrl = "SaveUser";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                toast.success("User Saved");
                //then need to go to job search?
                navigate("/usersearch");
            };
        }
        
    }

    var innercontents;
    if (tab == "Details") {
        innercontents = <UserDetail id={Number(id)} userDetails={userDetails} updateText={updateDetailsText} updateNumber={updateDetailsNumber} save={save} />
    }
    else if (tab == "Roles") {
        innercontents = <UserRole id={Number(id)} driverSystem={userDetails.driverSystem} skipTruckToolCheck={userDetails.skipTruckToolCheck} selectedUserRoles={userRoles} selectedUserBranches={userBranches} addRole={addRole} removeRole={removeRole} addBranch={addBranch} removeBranch={removeBranch} updateDriverSystem={updateDriverSystem} save={save} />
    }
    else if (tab == "Permissions") {
        innercontents = <UserPermission id={Number(id)} userPermissions={userPermissions} update={updatePermissions} save={save} />
    }

    const renderUser = (
        <div>
            <div className="tabsComponent" id="tab-container">

                <div className="tabsComponent__showDropdown">
                    <label className="input-group" htmlFor="userTab">
                        <span className="label">Item Type</span>
                        <select className="select" id="userTab" value={tab} onChange={tabChanged} >
                            <option value="Details">Details</option>
                            <option value="Roles">Roles/Branches</option>
                            <option value="Permissions">Permissions</option>
                        </select>
                    </label>
                </div>

                <div className="tabsComponent__tabs tabsComponent__hideTabs">
                    <input type="radio" name="tabs" id="tab1" className="tabsComponent__control" checked={tab == 'Details'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Details')}>Details</label>
                    <input type="radio" name="tabs" id="tab2" className="tabsComponent__control" checked={tab == 'Roles'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Roles')}>Roles/Branches</label>
                    <input type="radio" name="tabs" id="tab3" className="tabsComponent__control" checked={tab == 'Permissions'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Permissions')}>Permissions</label>
                </div>

                <div className="tabsComponent__content tabsComponent__content--selected">
                    <div>
                        {innercontents}
                    </div>
                </div>

            </div>

        </div>
    );
    
    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderUser;

    return <div>
        <h1>User</h1>
        {contents}
    </div>;
}

export default UserData;